import type {
  ThankYouNote,
  ThankYouNotesQuery,
  ThankYouNotesResponse,
} from "@/interfaces/thankYouNotes";
import { apiClient } from "@/lib/apiClient";

/**
 * The API wraps every payload as `{ success: true, ...result }`. Like `adminContacts`, this layer
 * hands the body back whole rather than unwrapping a `data` envelope — `success` simply rides along
 * as an extra key. It is declared locally (not in `interfaces/`) because nothing outside this file
 * reads it: callers consume `ThankYouNotesResponse`.
 */
type Enveloped<T> = T & { success: boolean };

/** One request per export page. Matches the backend's MAX_PAGE_SIZE and the Blotter's export walk. */
const EXPORT_PAGE_SIZE = 100;

/**
 * Safety cap for the export walk: 100 pages x 100 rows = 10k notes, the same ceiling `Blotter`'s
 * own CSV export uses. It exists so a server that never returns `nextCursor: null` cannot hang the
 * tab, not as a product limit — hitting it throws rather than returning a short CSV.
 */
const EXPORT_MAX_PAGES = 100;

const EXPORT_TOO_LARGE =
  "This export is larger than 10,000 notes. Narrow the filters and export again.";

/** First offset of a walk; `cursor` counts rows already held, so page one asks for 0. */
const FIRST_CURSOR = 0;

/**
 * GET /thank-you-notes — the feed's ONE read endpoint: search, campaign and the submitted date
 * range are SQL on the server (GFG-234), and the campaign dropdown + unfiltered count ride along
 * on the same response when `includeFilterOptions` is set.
 *
 * Only params the caller actually set are sent: an empty search or a cleared campaign must vanish
 * from the querystring, never travel as `""` — the backend compares `campaign` as an exact
 * campaign title, so a blank one would match zero rows and empty the feed.
 */
export async function fetchThankYouNotes(
  query: ThankYouNotesQuery = {},
): Promise<ThankYouNotesResponse> {
  const params: Record<string, string | number> = {};
  if (query.search) params.search = query.search;
  if (query.campaign) params.campaign = query.campaign;
  if (query.submittedFrom) params.submittedFrom = query.submittedFrom;
  if (query.submittedTo) params.submittedTo = query.submittedTo;
  // `cursor` 0 is the backend default, so page one leaves it off entirely.
  if (query.cursor) params.cursor = query.cursor;
  if (query.limit) params.limit = query.limit;
  if (query.includeFilterOptions) params.includeFilterOptions = "true";

  const { data } = await apiClient.get<Enveloped<ThankYouNotesResponse>>("/thank-you-notes", {
    params,
  });
  return data;
}

/**
 * PATCH /thank-you-notes/:id/favorite — a real, authenticated route that validates the body and
 * logs it server-side. It stores NOTHING: there is no favorites table (GFG-234 decision), so the
 * echoed `favorite` is just the value THIS call sent back at you, not a persisted state, and must
 * not be written back into UI state. The session's `favoriteIds` Set in `useThankYouNotes` remains
 * the source of truth; this call exists so the toggle is a genuine round-trip (auth, scope,
 * validation) and so persistence can be switched on later without touching the client.
 */
export async function toggleThankYouNoteFavorite(
  id: number,
  favorite: boolean,
): Promise<{ favorite: boolean }> {
  const { data } = await apiClient.patch<Enveloped<{ favorite: boolean }>>(
    `/thank-you-notes/${id}/favorite`,
    { favorite },
  );
  return data;
}

/**
 * Every note matching `query`, across every page — what the CSV export needs. The on-screen feed
 * holds one page, so exporting `data.notes` would silently ship a truncated file now that the
 * filters run server-side.
 *
 * `includeFilterOptions` is forced off: the dropdown lists are already on screen and the CSV does
 * not contain them, so paying for the reference queries once per page would be pure waste.
 */
export async function fetchAllThankYouNotes(query: ThankYouNotesQuery): Promise<ThankYouNote[]> {
  const notes: ThankYouNote[] = [];
  let cursor = query.cursor ?? FIRST_CURSOR;

  for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
    const result = await fetchThankYouNotes({
      ...query,
      cursor,
      limit: EXPORT_PAGE_SIZE,
      includeFilterOptions: false,
    });
    notes.push(...result.notes);

    const next = result.nextCursor;
    // null = exhausted. A cursor that does not move forward means the server would hand us the
    // same rows again on the next lap, so treat it as exhausted too rather than looping forever.
    if (next === null || next <= cursor) return notes;
    cursor = next;
  }

  // Ran out of laps with the server still offering more. Returning the rows collected so far would
  // hand the user a CSV that looks complete and is not, so fail loudly instead.
  throw new Error(EXPORT_TOO_LARGE);
}
